<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="h4 mb-0">Rapport des produits ({{ totalRows }})</h1>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">
        {{ errorMessage }}
      </div>

      <!-- Filtres -->
      <div class="card module-filter-card mb-3">
        <div class="card-body py-2">
          <form class="row g-2 align-items-end" @submit.prevent="applyFilters">
            <div class="col-md-3">
              <label class="form-label small fw-semibold mb-1">Rayon</label>
              <PrimevueDropdown
                v-model="filters.rayon_id"
                :options="rayonOptions"
                optionLabel="label"
                optionValue="value"
                class="w-100"
                filter
              >
                <template #value>
                  <span class="rayon-option">
                    <span
                      v-if="selectedRayonOption.value"
                      class="rayon-color-dot"
                      :style="{ backgroundColor: rayonColor(selectedRayonOption.color) }"
                    ></span>
                    <span>{{ selectedRayonOption.label }}</span>
                  </span>
                </template>
                <template #option="{ option }">
                  <span class="rayon-option">
                    <span
                      v-if="option.value"
                      class="rayon-color-dot"
                      :style="{ backgroundColor: rayonColor(option.color) }"
                    ></span>
                    <span>{{ option.label }}</span>
                  </span>
                </template>
              </PrimevueDropdown>
            </div>

            <div class="col-md-3">
              <label class="form-label small fw-semibold mb-1">Catégorie</label>
              <select
                v-model="filters.category_id"
                class="form-select form-select-sm"
              >
                <option value="">Toutes</option>
                <option
                  v-for="cat in categories"
                  :key="cat.id"
                  :value="cat.id"
                >
                  {{ cat.name }}
                </option>
              </select>
            </div>

            <div class="col-md-2">
              <label class="form-label small fw-semibold mb-1">Actifs</label>
              <select
                v-model="filters.only_active"
                class="form-select form-select-sm"
              >
                <option value="">Tous</option>
                <option value="true">Uniquement actifs</option>
              </select>
            </div>

            <div class="col-12 module-filter-actions mt-2">
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                @click="resetFilters"
                :disabled="isLoading"
              >
                <i class="fa fa-rotate-left me-1" aria-hidden="true"></i>
                Réinitialiser
              </button>
              <button
                type="submit"
                class="btn btn-primary btn-sm"
                :disabled="isLoading"
              >
                <i class="fa fa-search me-1"></i>
                Filtrer
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Résumé -->
      <div class="row mb-3">
        <div class="col-md-4 mb-2">
          <div class="card border-0 shadow-sm">
            <div class="card-body py-2">
              <div class="small text-muted">Nombre de produits</div>
              <div class="fs-5 fw-bold">{{ summary.total }}</div>
            </div>
          </div>
        </div>
        <div class="col-md-4 mb-2">
          <div class="card border-0 shadow-sm">
            <div class="card-body py-2">
              <div class="small text-muted">Stock total</div>
              <div class="fs-5 fw-bold">
                {{ summary.totalStock.toLocaleString() }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <BootstrapDataTable
        state-key="reports.products"
        :show-print="false"
        :rows="rows"
        :columns="tableColumns"
        :loading="isLoading"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        client-pagination
        :per-page="table.perPage"
        :active-filter-count="activeFilterCount"
        empty-text="Aucun produit trouvé."
        @sort-change="handleSortChange"
        @refresh="fetchReport"
      >
        <template #cell-rayon_name="{ row }">
          <span class="rayon-badge" :style="{ borderColor: rayonColor(row.rayon_color) }">
            <span class="rayon-color-dot" :style="{ backgroundColor: rayonColor(row.rayon_color) }"></span>
            <span>{{ row.rayon_name || '-' }}</span>
          </span>
        </template>

        <template #cell-sale_price="{ row }">
          {{ $format.currency(row.sale_price || 0) }}
        </template>

        <template #cell-sale_price_coefficient="{ row }">
          <div class="text-end">
            <span
              v-if="row.sale_price_coefficient !== null && row.sale_price_coefficient !== undefined"
              class="badge"
              :class="Number(row.sale_price_coefficient) <= 1 ? 'bg-danger' : 'bg-success'"
            >
              x{{ Number(row.sale_price_coefficient).toFixed(2) }}
            </span>
            <span v-else class="text-muted">—</span>
          </div>
        </template>

        <template #cell-is_active="{ row }">
          <span class="badge" :class="row.is_active ? 'bg-success' : 'bg-secondary'">
            {{ row.is_active ? 'Oui' : 'Non' }}
          </span>
        </template>
      </BootstrapDataTable>
    </template>
  </AdminLayout>
</template>

<script>
import { countActiveFilters } from '@/support/moduleFilters';
import { normalizeRayonColor } from '@/utils/rayonColor';

const DEFAULT_FILTERS = {
  rayon_id: '',
  category_id: '',
  only_active: '',
};

export default {
  name: 'ProductsReportView',
  data() {
    return {
      rows: [],
      isLoading: false,
      filters: { ...DEFAULT_FILTERS },
      rayons: [],
      categories: [],
      summary: {
        total: 0,
        totalStock: 0,
      },
      table: {
        sortBy: 'name',
        sortDir: 'asc',
        perPage: 10,
      },
      tableColumns: [
        { key: 'name', label: 'Produit', sortable: true },
        { key: 'sku', label: 'Code', sortable: true },
        { key: 'rayon_name', label: 'Rayon', sortable: true },
        { key: 'category_name', label: 'Catégorie', sortable: true },
        { key: 'stock_quantity', label: 'Stock', sortable: true, class: 'text-end' },
        { key: 'min_stock_quantity', label: 'Stock min.', sortable: true, class: 'text-end' },
        { key: 'sale_price', label: 'Prix vente', sortable: true, class: 'text-end' },
        { key: 'sale_price_coefficient', label: 'Coef.', sortable: true, class: 'text-end' },
        { key: 'is_active', label: 'Actif ?', sortable: true, class: 'text-center' },
      ],
      errorMessage: '',
    };
  },
  mounted() {
    this.fetchRayons();
    this.fetchCategories();
    this.fetchReport();
  },
  computed: {
    totalRows() {
      return Number(this.summary.total || this.rows.length || 0);
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, DEFAULT_FILTERS);
    },
    rayonOptions() {
      return [
        { label: 'Tous', value: '' },
        ...this.rayons.map((rayon) => ({
          label: rayon.name,
          value: String(rayon.id),
          color: rayon.color,
        })),
      ];
    },
    selectedRayonOption() {
      return this.rayonOptions.find((option) => option.value === String(this.filters.rayon_id || ''))
        || this.rayonOptions[0];
    },
  },
  methods: {
    async fetchRayons() {
      try {
        const res = await this.$api.get('/rayons', {
          params: { only_active: 'true' },
        });
        const data = res.data.data || res.data || [];
        this.rayons = data;
      } catch (e) {
        console.error('Erreur chargement rayons pour rapport produits', e);
      }
    },

    async fetchCategories() {
      try {
        const res = await this.$api.get('/categories', {
          params: { only_active: 'true' },
        });
        const data = res.data.data || res.data || [];
        this.categories = data;
      } catch (e) {
        console.error(
          'Erreur chargement catégories pour rapport produits',
          e
        );
      }
    },

    rayonColor(color) {
      return normalizeRayonColor(color);
    },

    async fetchReport() {
      this.isLoading = true;
      this.errorMessage = '';

      try {
        const params = {};
        if (this.filters.rayon_id) params.rayon_id = this.filters.rayon_id;
        if (this.filters.category_id)
          params.category_id = this.filters.category_id;
        if (this.filters.only_active)
          params.only_active = this.filters.only_active;

        const res = await this.$api.get('/reports/products', { params });
        const data = res.data.data || res.data;

        this.rows = data.rows || [];
        this.summary.total = data.total || this.rows.length;
        this.summary.totalStock = this.rows.reduce(
          (acc, r) => acc + (r.stock_quantity || 0),
          0
        );
      } catch (e) {
        console.error('Erreur rapport produits', e);
        this.errorMessage = 'Impossible de charger le rapport des produits.';
      } finally {
        this.isLoading = false;
      }
    },

    applyFilters() {
      this.fetchReport();
    },

    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
      this.fetchReport();
    },

    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
    },

  },
};
</script>

<style scoped>
.rayon-option,
.rayon-badge {
  align-items: center;
  display: inline-flex;
  gap: 0.4rem;
}

.rayon-badge {
  background: #ffffff;
  border: 1px solid;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
}

.rayon-color-dot {
  border: 1px solid rgba(15, 23, 42, 0.16);
  border-radius: 999px;
  display: inline-block;
  height: 0.75rem;
  width: 0.75rem;
}
</style>
